import { fmtDate, fmtDateLabel } from '../../shared/format';

/**
 * Toggl-style grouping for the timesheet: entries → weeks → days, newest first,
 * with per-day and per-week time totals.
 *
 * The stored entry carries `at` (a timestamp) and `minutes`, not an explicit
 * start/end pair — so the day an entry belongs to is `fmtDate(at)` in the reader's
 * local zone, and its clock range is derived from `at` + `minutes` at render time.
 *
 * Agent entries carry COST, not time, so they still render inside their day but add
 * zero to the time totals — a timesheet's running total is human time.
 */

// The Monday that starts the local week containing `dayKey` (a "YYYY-MM-DD"), as its
// own "YYYY-MM-DD". Timesheets read Monday-first; parsed at local midnight to dodge
// the UTC-shift gotcha the rest of format.js guards against.
export const weekStartKey = (dayKey) => {
  const d = new Date(`${dayKey}T00:00:00`);
  const mondayOffset = (d.getDay() + 6) % 7; // Sun=0 → 6, Mon=1 → 0, …
  d.setDate(d.getDate() - mondayOffset);
  return fmtDate(d);
};

/**
 * A correction writes a NEW row and keeps the original, which fn_my_timesheet may
 * return flagged `corrected: true`. That original is superseded: showing it would list
 * one piece of work twice and count its minutes beside the correction's. An entry
 * with `corrected` missing or false is live. If the RPC never returns superseded
 * originals, no entry matches and this is a no-op.
 *
 * Assumed, not probed: `corrected: true` marks the superseded original, not the
 * correction (see `__fixtures__/README.md`). If the backend means the opposite, this
 * hides every corrected entry. EffortContent logs a console error when the entry a
 * correction wrote comes back flagged.
 */
const isSuperseded = (e) => e?.corrected === true;

const minutesOf = (e) => (e?.actor_type === 'agent' ? 0 : Number(e?.minutes) || 0);

const sortByKeyDesc = (a, b) => {
  if (a.key === 'undated') return 1;
  if (b.key === 'undated') return -1;
  return a.key < b.key ? 1 : -1;
};

/**
 * @param {object[]} entries fn_my_timesheet entries. Superseded originals
 *   (`corrected: true`) are dropped before grouping, so they add no row and no minutes
 *   to a day or a week.
 * @returns {{key:string, minutes:number, days:{key:string, minutes:number, entries:object[]}[]}[]}
 *   weeks newest first, each with days newest first.
 */
export function groupTimesheet(entries) {
  const days = new Map();
  for (const e of (entries || []).filter((x) => !isSuperseded(x))) {
    const key = fmtDate(e?.at) || 'undated';
    if (!days.has(key)) days.set(key, { key, minutes: 0, entries: [] });
    const day = days.get(key);
    day.entries.push(e);
    day.minutes += minutesOf(e);
  }

  const weeks = new Map();
  for (const day of days.values()) {
    const wk = day.key === 'undated' ? 'undated' : weekStartKey(day.key);
    if (!weeks.has(wk)) weeks.set(wk, { key: wk, minutes: 0, days: [] });
    const week = weeks.get(wk);
    week.days.push(day);
    week.minutes += day.minutes;
  }

  const ordered = Array.from(weeks.values()).sort(sortByKeyDesc);
  ordered.forEach((w) => w.days.sort(sortByKeyDesc));
  return ordered;
}

// "Today" / "Yesterday" / "Fri, Sep 18" — relative to the reader's local today.
export const dayLabel = (key, now = new Date()) => {
  if (key === 'undated') return 'Undated';
  const today = fmtDate(now);
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (key === today) return 'Today';
  if (key === fmtDate(y)) return 'Yesterday';
  return fmtDateLabel(key);
};

// "This week" / "Last week" / "Week of Mon, Sep 8" — relative to the reader's week.
export const weekLabel = (weekKey, now = new Date()) => {
  if (weekKey === 'undated') return 'Undated';
  const thisWeek = weekStartKey(fmtDate(now));
  const prev = new Date(`${thisWeek}T00:00:00`);
  prev.setDate(prev.getDate() - 7);
  if (weekKey === thisWeek) return 'This week';
  if (weekKey === fmtDate(prev)) return 'Last week';
  return `Week of ${fmtDateLabel(weekKey)}`;
};
